import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import { getMarketingParams } from "../utils/queryParams";

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? "");

/**
 * Service for handling all Stripe-related API interactions
 */
export class StripeService {
  /**
   * Get the Stripe promise for initialization
   */
  static getStripe = () => stripePromise;

  /**
   * Update a payment intent with customer and order data
   */
  static async updatePaymentIntent(paymentIntentId: string, data: any): Promise<any> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/order-form/cart`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paymentIntentId,
            ...data,
          }),
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error("Error updating payment intent:", error);
      throw error;
    }
  }

  /**
   * Process a payment submission
   */
  static async handlePaymentSubmission({
    event,
    stripe,
    elements,
    paymentIntentId,
    clientSecret,
    customer,
    price,
    termsAccepted,
    skuPriceMap,
    selectedProduct,
    selectedPaymentMethodType,
    currency = "usd",
  }: {
    event: React.FormEvent;
    stripe: Stripe | null;
    elements: StripeElements | null;
    paymentIntentId: string;
    clientSecret: string;
    customer: any;
    price: number;
    termsAccepted: boolean;
    skuPriceMap: any;
    selectedProduct: any;
    selectedPaymentMethodType?: string;
    currency?: string;
  }): Promise<{ status: string; error?: string; paymentIntent?: any }> {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return { 
        status: "error", 
        error: "Stripe has not been loaded yet." 
      };
    }
    
    if (!termsAccepted) {
      return { 
        status: "error", 
        error: "You must accept the terms and conditions." 
      };
    }
    
    try {
      // Immediately call elements.submit() as Stripe requires this step before confirmPayment
      const submitResult = await elements.submit();
      if (submitResult.error) {
        return {
          status: "error",
          error: submitResult.error.message || "An unknown error occurred."
        };
      }
      
      let paymentMethodType = selectedPaymentMethodType;
      if (paymentMethodType === "google_pay" || paymentMethodType === "apple_pay") {
        paymentMethodType = "card";
      }
      
      // Get marketing parameters from URL if present
      const marketingParams = getMarketingParams();
      const { partnerCode, mbsy } = marketingParams;
      
      // Call the server-side endpoint to update the payment intent metadata
      await this.updatePaymentIntent(paymentIntentId, {
        selectedPaymentMethodType: paymentMethodType,
        amount: price,
        skuPriceMap,
        sku: selectedProduct.sku,
        termsAccepted,
        description: selectedProduct.name,
        first_name: customer?.firstName,
        last_name: customer?.lastName,
        email: customer?.email,
        phone: customer?.phone,
        address: customer?.address,
        currency: currency || "usd",
        // Include marketing parameters if they exist
        ...(partnerCode && { partnerCode }),
        ...(mbsy && { mbsy })
      });
      
      // Confirm the payment
      const confirmResult = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url:
            window.location.href +
            "?confirmed=true&confirmation_url=" +
            selectedProduct.confirmation_url,
        },
        redirect: "if_required",
      });
      
      if (confirmResult.error) {
        return {
          status: "error",
          error: confirmResult.error.message || "Payment failed"
        };
      }
      
      const paymentIntent = confirmResult.paymentIntent;
      
      return {
        status: "success",
        paymentIntent
      };
    } catch (error: any) {
      return {
        status: "error",
        error: error.message || "Error processing payment"
      };
    }
  }
}

export default StripeService;